import type { DataTableColumn } from "@etudescarre/ui";
import { useEffect } from "react";
import { useParams } from "react-router";
import { apiClient } from "@/lib/api/client";
import { formatDay } from "@/lib/utils/date";
import { Classeur, type FilterDef } from "../components/classeur";
import { ClasseurPdfButton } from "../components/classeur/classeur-pdf-button";
import {
  useClasseurFilters,
  useClasseurState,
} from "../components/classeur/use-classeur-data";

type VisaItem = {
  id: string;
  numero: string | null;
  dateEnvoi: string | null;
  contractCode: string | null;
  recipientName: string | null;
  fichierId: number | null;
  authorEmployee: string | null;
  documents: { number: string; index: string | null }[];
};

const columns: DataTableColumn<VisaItem>[] = [
  { id: "numero", header: "N°", accessor: "numero" },
  {
    id: "dateEnvoi",
    header: "Date d'envoi",
    accessor: "dateEnvoi",
    cell: ({ value }) => (value ? formatDay(value as string) : "-"),
  },
  {
    id: "contract",
    header: "Contrat",
    accessor: (row) => row.recipientName ?? row.contractCode,
  },
  { id: "authorEmployee", header: "Auteur", accessor: "authorEmployee" },
  {
    id: "documents",
    header: "Documents",
    accessor: (row) =>
      row.documents
        .map(({ number, index }) => [number, index].filter(Boolean).join(" "))
        .join(", "),
  },
  {
    id: "pdf",
    header: "PDF",
    accessor: "fichierId",
    cell: ({ row }) => (
      <ClasseurPdfButton
        fichierId={row.fichierId}
        label={`le visa ${row.numero ?? ""}`}
      />
    ),
  },
];

const filters: FilterDef[] = [];

export function ClasseurVisas() {
  const { yardId } = useParams<{ yardId: string }>();
  const { activeFilters, onFilterChange } = useClasseurFilters({ filters });
  const [state, setState] = useClasseurState<VisaItem>();

  useEffect(() => {
    async function fetchData() {
      setState((prev) => ({ ...prev, isLoading: true }));
      const { data } = await apiClient.GET("/api/yards/{yardId}/visas", {
        params: {
          path: { yardId: yardId ?? "" },
          query: { direction: "emitted" },
        },
      });
      if (data) {
        const items = data.data.items.map((item) => ({
          ...item,
          id: String(item.id),
        }));
        setState({ items, total: data.data.total, isLoading: false });
      } else {
        setState({ items: [], total: 0, isLoading: false });
      }
    }
    fetchData();
  }, [yardId, setState]);

  return (
    <Classeur
      title="Classeur visas entreprise générale"
      items={state.items}
      columns={columns}
      filters={filters}
      activeFilters={activeFilters}
      onFilterChange={onFilterChange}
      isLoading={state.isLoading}
      emptyMessage="Aucun visa"
      total={state.total}
    />
  );
}
